import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Users, Plus, Search, Filter, MoreVertical, Eye, Edit, Trash2,
  Download, Upload, Mail, Phone, Building2, GraduationCap, FileSpreadsheet,
  CheckCircle, XCircle, AlertTriangle, UserX, UserCheck, Loader2, KeyRound, Copy, ShieldAlert,
  ArrowUp, ArrowDown, ListOrdered, Table2, ListChecks
} from 'lucide-react';
import { toast } from 'sonner';

import { API_BASE as API_URL } from '../lib/apiBase';
import { errorMessage } from '../lib/errorMessage';
import { EmployeeMasterGrid } from '../components/master/EmployeeMasterGrid';
import { MasterDataManager } from '../components/master/MasterDataManager';
import { computeMaster } from '../lib/salaryCalc';
const EMPLOYEE_CATEGORIES = [
  "Professor", "Associate Professor", "Assistant Professor",
  "Lecturer", "Visiting Faculty", "School Teacher",
  "Coaching Faculty", "Lab Instructor", "Non-teaching Staff",
  "Contract/Adhoc Faculty", "Guest Faculty"
];

const APPOINTMENT_TYPES = ["Permanent", "Contract", "Adhoc", "Guest"];

const StatutoryPreview = ({ f, claCfg }) => {
  if ((f.salary_type || 'breakdown') === 'consolidated') return null;
  const c = computeMaster(f, null, claCfg);
  const rows = [['DA', c.da_amount], ['HRA', c.hra_amount],
    ['GROSS', c.gross_salary], ['PF (12%)', c.pf_amount], ['Prof. Tax', c.profession_tax_amount],
    ['ESIC', c.esic_amount], ['Total Deductions', c.total_deductions], ['NET', c.net_salary]];
  return (
    <div className="rounded-md border border-indigo-200 bg-indigo-50/50 p-2.5" data-testid="statutory-preview">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-indigo-500 mb-1.5">Auto-Calculation Preview (live)</p>
      <div className="flex flex-wrap gap-2">
        {rows.map(([l, v]) => (
          <div key={l} className={`px-2 py-1 rounded bg-white border text-xs ${(l === 'GROSS' || l === 'NET') ? 'border-indigo-400 font-bold' : 'border-slate-200'}`}>
            <span className="text-slate-500">{l}:</span> <span className="font-mono">₹{Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function Employees() {
  const navigate = useNavigate();
  const { selectedEntity, entities } = useOutletContext() || {};
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [viewMode, setViewMode] = useState('directory');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [reorderOpen, setReorderOpen] = useState(false);
  const [seqLock, setSeqLock] = useState({ locked: false });
  const [moveDept, setMoveDept] = useState('');
  const [reorderList, setReorderList] = useState([]);
  const [reorderChecked, setReorderChecked] = useState({});
  const [movePos, setMovePos] = useState('');
  const [deptSeq, setDeptSeq] = useState([]);
  const [showDeptSeq, setShowDeptSeq] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [uploadResults, setUploadResults] = useState(null);
  const fileInputRef = useRef(null);
  const [exitDialogOpen, setExitDialogOpen] = useState(null);
  const [exitForm, setExitForm] = useState({ exit_date: new Date().toISOString().slice(0, 10), reason: 'Resignation', remarks: '' });
  
  // Roles that may manage employees. Branch / institute admins are now included
  // (Iter 58: backend enforces subtree scoping, so frontend can safely allow the buttons).
  const STAFF_MGMT_ROLES = ['super_admin', 'admin', 'hr', 'branch_admin'];
  const canAddEmployee = STAFF_MGMT_ROLES.includes(user?.role);
  const canEditEmployee = STAFF_MGMT_ROLES.includes(user?.role);
  const canExitEmployee = STAFF_MGMT_ROLES.includes(user?.role);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [bulkDesigOpen, setBulkDesigOpen] = useState(false);
  const [bulkDesigData, setBulkDesigData] = useState({ employee_ids: [], designation: '' });
  const [selectedRows, setSelectedRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [addMasterLists, setAddMasterLists] = useState({});

  // Password reset — dialog for single or multi employees, inline JSON for single, Excel for multi
  const [resetOpen, setResetOpen] = useState(false);
  const [resetTargets, setResetTargets] = useState([]); // [{employee_id, name, email}]
  const [resetting, setResetting] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const defaultFormData = {
    entity_id: '', first_name: '', middle_name: '', last_name: '', email: '', phone: '',
    gender: '', date_of_birth: '', category: '', department: '', designation: '',
    date_of_joining: '', appointment_type: 'Permanent',
    salary_type: 'breakdown', consolidated_salary: '',
    gross_salary: '', basic_salary: '', agp: '', da: '', da_percentage: '', hra: '', cla: '',
    hod_allowance: '', special_allowance: '', other_allowance: '',
    provident_fund: '', esic: '', profession_tax: '', bus: '', accommodation: '', tds: '', localmart: '', other_deduction: '',
    pf_applicable: true, pt_applicable: true, local_mart_applicable: true,
    esic_applicable: false, is_handicapped: false, is_senior_citizen: false,
    highest_qualification: '', specialization: '', experience_years: 0, address: '',
    bank_name: '', bank_account: '', ifsc_code: ''
  };
  const [formData, setFormData] = useState(defaultFormData);
  const [claMap, setClaMap] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('hrms-token');
        const res = await fetch(`${API_URL}/api/payroll/cla-settings`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}, credentials: 'include',
        });
        if (!res.ok) return;
        const d = await res.json();
        const m = {};
        (Array.isArray(d) ? d : []).forEach(r => { m[r.entity_id] = { enabled: r.enabled, teaching: r.teaching, non_teaching: r.non_teaching }; });
        setClaMap(m);
      } catch { /* preview falls back to default 800/400 */ }
    })();
  }, []);

  useEffect(() => {
    fetchEmployees();
  }, [selectedEntity, categoryFilter, statusFilter]);

  // Load the campus master lists for the Add-Employee dialog so Department / Designation /
  // Category / Appointment Type offer only approved values (single source of truth, Req 6).
  useEffect(() => {
    const ent = formData.entity_id || selectedEntity?.entity_id;
    if (!isDialogOpen || !ent) return;
    (async () => {
      try {
        const token = localStorage.getItem('hrms-token');
        const res = await fetch(`${API_URL}/api/master-data?entity_id=${ent}`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}, credentials: 'include',
        });
        if (!res.ok) { setAddMasterLists({}); return; }
        const d = await res.json();
        const e = d.entries || {};
        const vals = (t) => (e[t] || []).filter(x => x.active !== false).map(x => x.value);
        setAddMasterLists({
          department: vals('department'), designation: vals('designation'),
          category: vals('category'), appointment_type: vals('appointment_type'),
        });
      } catch { setAddMasterLists({}); }
    })();
  }, [isDialogOpen, formData.entity_id, selectedEntity]);

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('hrms-token');
      let url = `${API_URL}/api/employees`;
      const params = new URLSearchParams();
      
      if (selectedEntity) params.append('entity_id', selectedEntity.entity_id);
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      if (statusFilter) params.append('status', statusFilter);
      
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await fetch(url, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setEmployees(data);
        // Build dynamic category list from actual data
        const cats = [...new Set(data.map(e => e.category).filter(Boolean))].sort();
        setCategories(cats);
      }
    } catch (error) {
      console.error('Failed to fetch employees:', error);
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const openReorder = async () => {
    setReorderList([...employees]);
    setDeptSeq([...new Set(employees.map(e => e.department).filter(Boolean))]);
    setShowDeptSeq(false);
    setReorderChecked({});
    setMovePos('');
    setMoveDept('');
    setReorderOpen(true);
    // sequence lock state
    try {
      const token = localStorage.getItem('hrms-token');
      const res = await fetch(`${API_URL}/api/employee-sequence-lock?entity_id=${selectedEntity?.entity_id}`,
        { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) }, credentials: 'include' });
      const d = await res.json();
      setSeqLock(res.ok ? d : { locked: false });
    } catch { setSeqLock({ locked: false }); }
  };

  const toggleSeqLock = async (action) => {
    try {
      const token = localStorage.getItem('hrms-token');
      const res = await fetch(`${API_URL}/api/employee-sequence-lock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: 'include',
        body: JSON.stringify({ entity_id: selectedEntity?.entity_id, action })
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Failed');
      toast.success(d.message);
      setSeqLock({ ...seqLock, locked: d.locked });
    } catch (e) { toast.error(e.message); }
  };

  const moveSelectedToDepartment = () => {
    if (!moveDept) { toast.error('Choose a department'); return; }
    const selected = reorderList.filter(e => reorderChecked[e.employee_id]);
    if (selected.length === 0) { toast.error('Tick at least one employee to move'); return; }
    const rest = reorderList.filter(e => !reorderChecked[e.employee_id]);
    let lastIdx = -1;
    rest.forEach((e, i) => { if ((e.department || '') === moveDept) lastIdx = i; });
    const insertAt = lastIdx >= 0 ? lastIdx + 1 : rest.length;
    setReorderList([...rest.slice(0, insertAt), ...selected, ...rest.slice(insertAt)]);
    setReorderChecked({});
    toast.success(`Moved ${selected.length} employee(s) into ${moveDept}`);
  };

  const moveSelectedToPosition = () => {
    const selected = reorderList.filter(e => reorderChecked[e.employee_id]);
    if (selected.length === 0) { toast.error('Tick at least one employee to move'); return; }
    let pos = parseInt(movePos, 10);
    if (isNaN(pos)) { toast.error('Enter a target position number'); return; }
    const rest = reorderList.filter(e => !reorderChecked[e.employee_id]);
    pos = Math.max(1, Math.min(pos, rest.length + 1));
    const next = [...rest.slice(0, pos - 1), ...selected, ...rest.slice(pos - 1)];
    setReorderList(next);
    setReorderChecked({});
    setMovePos('');
    toast.success(`Moved ${selected.length} employee(s) to position ${pos}`);
  };

  const moveRow = (idx, dir) => {
    const next = [...reorderList];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setReorderList(next);
  };

  const moveDeptSeq = (idx, dir) => {
    const next = [...deptSeq];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setDeptSeq(next);
  };

  const applyDeptSequence = () => {
    const order = {};
    deptSeq.forEach((d, i) => { order[d] = i; });
    setReorderList(prev => {
      const idxMap = new Map(prev.map((e, i) => [e.employee_id, i]));
      return [...prev].sort((a, b) => {
        const ao = order[a.department || ''] ?? 9999;
        const bo = order[b.department || ''] ?? 9999;
        if (ao !== bo) return ao - bo;
        return idxMap.get(a.employee_id) - idxMap.get(b.employee_id);
      });
    });
    toast.success('Staff regrouped by department sequence — fine-tune below, then Save');
  };

  const applyQuickSort = (mode) => {
    if (mode === 'dept_category') {
      setReorderList(prev => [...prev].sort((a, b) => {
        const ad = (a.department || 'zzz').toLowerCase(), bd = (b.department || 'zzz').toLowerCase();
        if (ad !== bd) return ad.localeCompare(bd);
        const at = (a.category || '').toLowerCase().includes('non') ? 1 : 0;
        const bt = (b.category || '').toLowerCase().includes('non') ? 1 : 0;
        if (at !== bt) return at - bt;
        return (a.first_name || '').toLowerCase().localeCompare((b.first_name || '').toLowerCase());
      }));
      return;
    }
    const key = { department: 'department', designation: 'designation', category: 'category', name: 'first_name' }[mode];
    if (!key) return;
    setReorderList(prev => [...prev].sort((a, b) => {
      const av = (a[key] || 'zzz').toString().toLowerCase();
      const bv = (b[key] || 'zzz').toString().toLowerCase();
      if (av !== bv) return av.localeCompare(bv);
      return (a.first_name || '').toLowerCase().localeCompare((b.first_name || '').toLowerCase());
    }));
  };

  const handleSaveOrder = async () => {
    setSavingOrder(true);
    try {
      const token = localStorage.getItem('hrms-token');
      const res = await fetch(`${API_URL}/api/employees/reorder`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        credentials: 'include',
        body: JSON.stringify({ orders: reorderList.map((e, i) => ({ employee_id: e.employee_id, display_order: i + 1 })) })
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Failed to save order');
      toast.success(`Sequence saved & LOCKED for ${d.updated} employees — unlock required for further changes`);
      setReorderOpen(false);
      fetchEmployees();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSavingOrder(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('hrms-token');
      const response = await fetch(`${API_URL}/api/employees`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          ...formData,
          consolidated_salary: parseFloat(formData.consolidated_salary) || 0,
          basic_salary: parseFloat(formData.basic_salary) || 0,
          gross_salary: parseFloat(formData.gross_salary) || 0,
          agp: parseFloat(formData.agp) || 0,
          da: parseFloat(formData.da) || 0,
          da_percentage: parseFloat(formData.da_percentage) || 0,
          hra: parseFloat(formData.hra) || 0,
          cla: parseFloat(formData.cla) || 0,
          hod_allowance: parseFloat(formData.hod_allowance) || 0,
          special_allowance: parseFloat(formData.special_allowance) || 0,
          other_allowance: parseFloat(formData.other_allowance) || 0,
          provident_fund: parseFloat(formData.provident_fund) || 0,
          esic: parseFloat(formData.esic) || 0,
          profession_tax: parseFloat(formData.profession_tax) || 0,
          bus: parseFloat(formData.bus) || 0,
          accommodation: parseFloat(formData.accommodation) || 0,
          tds: parseFloat(formData.tds) || 0,
          localmart: parseFloat(formData.localmart) || 0,
          other_deduction: parseFloat(formData.other_deduction) || 0,
          pf_applicable: formData.pf_applicable !== false,
          pt_applicable: formData.pt_applicable !== false,
          local_mart_applicable: formData.local_mart_applicable !== false,
          esic_applicable: formData.esic_applicable === true,
          is_handicapped: formData.is_handicapped === true,
          is_senior_citizen: formData.is_senior_citizen === true,
          experience_years: parseFloat(formData.experience_years) || 0,
          entity_id: formData.entity_id || selectedEntity?.entity_id || entities?.[0]?.entity_id
        }),
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        if (data.auto_user_created && data.default_password) {
          // Show credentials prominently so HR can share with the employee
          toast.success(
            `Employee added! Login: ${data.login_email} · Temp password: ${data.default_password} (must change on first login)`,
            { duration: 15000 }
          );
        } else {
          toast.success('Employee added successfully');
        }
        setIsDialogOpen(false);
        setFormData(defaultFormData);
        fetchEmployees();
      } else {
        const error = await response.json();
        toast.error(errorMessage(error.detail, 'Failed to add employee'));
      }
    } catch (error) {
      toast.error('Failed to add employee');
    }
  };

  const handleDelete = async (employeeId, permanent = false) => {
    const msg = permanent
      ? 'PERMANENTLY DELETE this employee and ALL their records (attendance, leaves, face registration)? This cannot be undone!'
      : 'Are you sure you want to deactivate this employee?';
    if (!confirm(msg)) return;
    
    try {
      const token = localStorage.getItem('hrms-token');
      const url = permanent
        ? `${API_URL}/api/employees/${employeeId}?permanent=true`
        : `${API_URL}/api/employees/${employeeId}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success(permanent ? 'Employee permanently deleted' : 'Employee deactivated');
        fetchEmployees();
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(errorMessage(err.detail, 'Failed to delete employee'));
      }
    } catch (error) {
      toast.error('Failed to delete employee');
    }
  };

  const handleReactivate = async (employeeId) => {
    if (!confirm('Reactivate this employee? Their record and login will be restored to active.')) return;
    try {
      const token = localStorage.getItem('hrms-token');
      const response = await fetch(`${API_URL}/api/employees/${employeeId}/reactivate`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Employee reactivated');
        fetchEmployees();
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(errorMessage(err.detail, 'Failed to reactivate employee'));
      }
    } catch (error) {
      toast.error('Failed to reactivate employee');
    }
  };

  const handleExitEmployee = async () => {
    if (!exitDialogOpen) return;
    try {
      const token = localStorage.getItem('hrms-token');
      const params = new URLSearchParams({
        exit_date: exitForm.exit_date,
        reason: exitForm.reason,
        remarks: exitForm.remarks
      });
      const res = await fetch(`${API_URL}/api/employees/${exitDialogOpen}/exit?${params}`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include'
      });
      if (res.ok) {
        toast.success('Employee exit processed');
        setExitDialogOpen(null);
        fetchEmployees();
      } else {
        const err = await res.json();
        toast.error(errorMessage(err.detail, 'Failed to process exit'));
      }
    } catch { toast.error('Failed to process exit'); }
  };

  const downloadLetter = async (employeeId, type) => {
    try {
      const token = localStorage.getItem('hrms-token');
      const res = await fetch(`${API_URL}/api/employees/${employeeId}/${type}-letter`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include'
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = `${type}_letter_${employeeId}.pdf`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a); window.URL.revokeObjectURL(url);
        toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} letter downloaded`);
      } else toast.error('Failed to download letter');
    } catch { toast.error('Failed to download'); }
  };

  const handleExportCSV = () => {
    const list = filteredEmployees;
    if (!list || list.length === 0) {
      toast.error('No employees to export in the current view');
      return;
    }
    const cols = [
      { key: 'employee_id', label: 'Employee ID' },
      { key: 'first_name', label: 'First Name' },
      { key: 'last_name', label: 'Last Name' },
      { key: 'email', label: 'Email' },
      { key: 'phone', label: 'Phone' },
      { key: 'department', label: 'Department' },
      { key: 'designation', label: 'Designation' },
      { key: 'category', label: 'Category' },
      { key: 'appointment_type', label: 'Appointment Type' },
      { key: 'entity_id', label: 'Entity ID' },
      { key: 'entity_name', label: 'Entity Name' },
      { key: 'date_of_joining', label: 'Date of Joining' },
      { key: 'date_of_birth', label: 'Date of Birth' },
      { key: 'gender', label: 'Gender' },
      { key: 'blood_group', label: 'Blood Group' },
      { key: 'is_active', label: 'Active' },
    ];
    const escape = (v) => {
      if (v === null || v === undefined) return '';
      const s = String(v);
      // Wrap in quotes if it contains a comma, quote, or newline
      if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const header = cols.map((c) => escape(c.label)).join(',');
    const body = list.map((emp) => cols.map((c) => escape(emp[c.key])).join(',')).join('\n');
    // Prepend BOM so Excel opens UTF-8 correctly
    const csv = '\ufeff' + header + '\n' + body;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `employees_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success(`Exported ${list.length} employees to CSV`);
  };

  const [exportingFull, setExportingFull] = useState(false);

  // Complete employee master — every stored field, straight from the server (HR/Admin only)
  const handleFullExport = async () => {
    setExportingFull(true);
    try {
      const token = localStorage.getItem('hrms-token');
      const params = new URLSearchParams({ status: 'all', include_descendants: 'true' });
      if (selectedEntity?.entity_id) params.append('entity_id', selectedEntity.entity_id);
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      const res = await fetch(`${API_URL}/api/employees/full-export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }, credentials: 'include'
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Download failed');
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `employee_master_full_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Complete employee data downloaded');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setExportingFull(false);
    }
  };

  const openResetSingle = (emp) => {
    setResetResult(null);
    setResetTargets([{ employee_id: emp.employee_id, name: `${emp.first_name || ''} ${emp.last_name || ''}`.trim(), email: emp.email }]);
    setResetOpen(true);
  };

  const openResetBulk = () => {
    setResetResult(null);
    const targets = filteredEmployees
      .filter((e) => selectedRows.includes(e.employee_id))
      .map((e) => ({ employee_id: e.employee_id, name: `${e.first_name || ''} ${e.last_name || ''}`.trim(), email: e.email }));
    if (targets.length === 0) { toast.error('Select employees first'); return; }
    setResetTargets(targets);
    setResetOpen(true);
  };

  const submitReset = async () => {
    if (resetTargets.length === 0) return;
    const isSingle = resetTargets.length === 1;
    setResetting(true);
    try {
      const token = localStorage.getItem('hrms-token');
      const res = await fetch(`${API_URL}/api/employees/reset-passwords`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        credentials: 'include',
        body: JSON.stringify({ employee_ids: resetTargets.map((t) => t.employee_id), inline: isSingle }),
      });
      if (!res.ok) {
        const text = await res.text();
        let detail = `Reset failed (HTTP ${res.status})`;
        try { const j = JSON.parse(text); detail = j.detail || detail; } catch { /* non-json */ }
        throw new Error(detail);
      }
      if (isSingle) {
        const data = await res.json();
        setResetResult(data);
        toast.success('Password reset — copy the new credentials below');
      } else {
        const resetCount = res.headers.get('x-reset') || '0';
        const createdCount = res.headers.get('x-created') || '0';
        const skippedNoEmail = res.headers.get('x-skipped-no-email') || '0';
        const errCount = res.headers.get('x-errors') || '0';
        const blob = await res.blob();
        const dl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = dl;
        a.download = `password_reset_${new Date().toISOString().slice(0, 10)}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(dl);
        toast.success(`Passwords ready — ${resetCount} reset, ${createdCount} newly created, ${skippedNoEmail} skipped (no email), ${errCount} error(s)`);
        setResetOpen(false);
        setResetTargets([]);
        setSelectedRows([]);
      }
    } catch (e) {
      toast.error(e.message || 'Password reset failed');
    } finally {
      setResetting(false);
    }
  };

  const copyResetCred = (cred) => {
    const txt = `Login URL: ${window.location.origin}/login\nEmail: ${cred.email}\nPassword: ${cred.password}`;
    navigator.clipboard?.writeText(txt);
    toast.success('Credentials copied to clipboard');
  };

  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem('hrms-token');
      const response = await fetch(`${API_URL}/api/employees/bulk-upload/template`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'employee_upload_template.xlsx';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('Template downloaded successfully');
      } else {
        toast.error('Failed to download template');
      }
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadProgress('uploading');
    setUploadResults(null);
    
    try {
      const token = localStorage.getItem('hrms-token');
      const formData = new FormData();
      formData.append('file', file);
      if (selectedEntity?.entity_id) {
        formData.append('entity_id', selectedEntity.entity_id);
      }
      
      const response = await fetch(`${API_URL}/api/employees/bulk-upload`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData,
        credentials: 'include'
      });
      
      const result = await response.json();
      
      if (response.ok) {
        setUploadResults(result.results);
        setUploadProgress('complete');
        toast.success(result.message);
        fetchEmployees();
      } else {
        setUploadProgress('error');
        toast.error(errorMessage(result.detail, 'Upload failed'));
      }
    } catch (error) {
      setUploadProgress('error');
      toast.error('Failed to upload file');
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleBulkDateCorrection = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const token = localStorage.getItem('hrms-token');
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/employees/bulk-correct-dates`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: fd,
        credentials: 'include'
      });
      const result = await res.json();
      if (res.ok) {
        toast.success(result.message || 'Dates corrected');
        if (result.errors?.length) {
          console.warn('Bulk date correction errors:', result.errors);
          toast.warning(`${result.errors.length} row(s) had errors - check console`);
        }
        fetchEmployees();
      } else {
        toast.error(errorMessage(result.detail, 'Correction failed'));
      }
    } catch (err) {
      toast.error('Failed to correct dates');
    }
    e.target.value = '';
  };

  const filteredEmployees = employees.filter(emp => {
    const searchLower = searchTerm.toLowerCase();
    const matchSearch = !searchTerm || (
      emp.first_name?.toLowerCase().includes(searchLower) ||
      emp.last_name?.toLowerCase().includes(searchLower) ||
      emp.email?.toLowerCase().includes(searchLower) ||
      emp.employee_id?.toLowerCase().includes(searchLower) ||
      emp.department?.toLowerCase().includes(searchLower) ||
      emp.designation?.toLowerCase().includes(searchLower)
    );
    return matchSearch;
  });

  const handleEditEmployee = async () => {
    if (!editingEmployee) return;
    try {
      const token = localStorage.getItem('hrms-token');
      const res = await fetch(`${API_URL}/api/employees/${editingEmployee.employee_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: JSON.stringify(editingEmployee),
        credentials: 'include'
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Update failed'); }
      toast.success('Employee updated successfully');
      setEditDialogOpen(false);
      setEditingEmployee(null);
      fetchEmployees();
    } catch (e) { toast.error(e.message); }
  };

  const [bulkEditData, setBulkEditData] = useState({}); // { field_key: value } for enabled fields
  const [bulkEditEnabled, setBulkEditEnabled] = useState({}); // { field_key: bool } — only enabled fields are applied

  const handleBulkFieldUpdate = async () => {
    const ids = selectedRows.length > 0 ? selectedRows : bulkDesigData.employee_ids;
    if (ids.length === 0) { toast.error('Select employees first'); return; }
    // Assemble the payload from only the enabled fields
    const payload = {};
    for (const key of Object.keys(bulkEditEnabled)) {
      if (!bulkEditEnabled[key]) continue;
      const v = bulkEditData[key];
      // Numeric fields → parseFloat; boolean flags → as-is
      const numericFields = new Set(['basic_salary','agp','da','da_percentage','hra','cla','hod_allowance','special_allowance','other_allowance','provident_fund','esic','profession_tax','tds','bus','accommodation','localmart','other_deduction','consolidated_salary']);
      const boolFields = new Set(['pf_applicable','pt_applicable','local_mart_applicable','esic_applicable','is_handicapped','is_senior_citizen']);
      const timeFields = new Set(['late_check_in_after','morning_half_day_after']);
      if (numericFields.has(key)) payload[key] = parseFloat(v) || 0;
      else if (boolFields.has(key)) payload[key] = Boolean(v);
      else if (timeFields.has(key)) payload[key] = (v || '').trim() || null;
      else payload[key] = v;
    }
    if (Object.keys(payload).length === 0) {
      toast.error('Tick at least one field to update');
      return;
    }
    try {
      const token = localStorage.getItem('hrms-token');
      let successCount = 0;
      for (const empId of ids) {
        const res = await fetch(`${API_URL}/api/employees/${empId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
          body: JSON.stringify(payload),
          credentials: 'include'
        });
        if (res.ok) successCount++;
      }
      toast.success(`Updated ${Object.keys(payload).length} field(s) for ${successCount}/${ids.length} employees`);
      setBulkDesigOpen(false);
      setBulkEditData({});
      setBulkEditEnabled({});
      setBulkDesigData({ employee_ids: [], designation: '' });
      setSelectedRows([]);
      fetchEmployees();
    } catch (e) { toast.error(e.message || 'Bulk update failed'); }
  };

  const handleBulkDesignationUpdate = async () => {
    const ids = selectedRows.length > 0 ? selectedRows : bulkDesigData.employee_ids;
    if (ids.length === 0 || !bulkDesigData.designation) {
      toast.error('Select employees and enter designation');
      return;
    }
    try {
      const token = localStorage.getItem('hrms-token');
      let successCount = 0;
      for (const empId of ids) {
        const res = await fetch(`${API_URL}/api/employees/${empId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
          body: JSON.stringify({ designation: bulkDesigData.designation }),
          credentials: 'include'
        });
        if (res.ok) successCount++;
      }
      toast.success(`Designation updated for ${successCount} employees`);
      setBulkDesigOpen(false);
      setBulkDesigData({ employee_ids: [], designation: '' });
      setSelectedRows([]);
      fetchEmployees();
    } catch (e) { toast.error(e.message); }
  };

  const toggleRowSelect = (empId) => {
    setSelectedRows(prev => prev.includes(empId) ? prev.filter(id => id !== empId) : [...prev, empId]);
  };

  const toggleSelectAll = () => {
    if (selectedRows.length === filteredEmployees.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(filteredEmployees.map(e => e.employee_id));
    }
  };

  const openEditDialog = (emp) => {
    setEditingEmployee({ ...emp });
    setEditDialogOpen(true);
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  const getCategoryBadgeColor = (category) => {
    const colors = {
      'Professor': 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
      'Associate Professor': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      'Assistant Professor': 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400',
      'Lecturer': 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400',
      'School Teacher': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      'Non-teaching Staff': 'bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-400',
    };
    return colors[category] || 'bg-slate-100 text-slate-800';
  };

  if (viewMode === 'master') {
    return (
      <div className="space-y-6 animate-fade-in" data-testid="employees-master-page">
        <EmployeeMasterGrid
          entityId={selectedEntity?.entity_id}
          onBack={() => { setViewMode('directory'); fetchEmployees(); }}
        />
      </div>
    );
  }

  if (viewMode === 'masterData') {
    return (
      <div className="space-y-6 animate-fade-in" data-testid="employees-masterdata-page">
        <MasterDataManager
          entityId={selectedEntity?.entity_id}
          onBack={() => { setViewMode('directory'); fetchEmployees(); }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="employees-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">
            Employee Management
          </h1>
          <p className="text-slate-500 dark:text-slate-400">
            Manage faculty and staff across all entities
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => setViewMode('master')}
          disabled={!selectedEntity?.entity_id}
          title={selectedEntity?.entity_id ? 'Open the editable master data table' : 'Select an institute / branch first'}
          data-testid="open-master-grid-btn"
        >
          <Table2 className="w-4 h-4 mr-1.5" /> Master Data (Editable)
        </Button>
        <Button
          variant="outline"
          onClick={() => setViewMode('masterData')}
          disabled={!selectedEntity?.entity_id}
          title={selectedEntity?.entity_id ? 'Manage Department / Designation / Category lists' : 'Select an institute / branch first'}
          data-testid="open-master-data-btn"
        >
          <ListChecks className="w-4 h-4 mr-1.5" /> Manage Master Data
        </Button>
        {canAddEmployee && (
          <div className="flex gap-2 flex-wrap">
            {selectedRows.length > 0 && (
              <>
                <Button variant="outline" className="border-indigo-400 text-indigo-700 hover:bg-indigo-50" onClick={() => setBulkDesigOpen(true)} data-testid="bulk-designation-btn">
                  <Edit className="w-4 h-4 mr-2" />
                  Bulk Edit ({selectedRows.length})
                </Button>
                <Button variant="outline" className="border-amber-500 text-amber-700 hover:bg-amber-50" onClick={openResetBulk} data-testid="bulk-reset-pwd-btn">
                  <KeyRound className="w-4 h-4 mr-2" />
                  Reset Password ({selectedRows.length})
                </Button>
              </>
            )}
            {/* Export CSV — exports the currently-filtered employees */}
            <Button
              variant="outline"
              className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
              onClick={handleExportCSV}
              data-testid="export-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            {['super_admin', 'admin', 'hr'].includes(user?.role) && (
              <Button
                variant="outline"
                className="border-emerald-700 text-emerald-800 hover:bg-emerald-50"
                onClick={handleFullExport}
                disabled={exportingFull}
                data-testid="export-full-btn"
              >
                {exportingFull ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Download All Data
              </Button>
            )}
            <Button
              variant="outline"
              className="border-blue-500 text-blue-700 hover:bg-blue-50"
              onClick={openReorder}
              data-testid="reorder-btn"
            >
              <ListOrdered className="w-4 h-4 mr-2" />
              Rearrange
            </Button>
            {/* Bulk Upload Dialog */}
            <Dialog open={isBulkUploadOpen} onOpenChange={setIsBulkUploadOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-[#DC2626] text-[#DC2626] hover:bg-red-50" data-testid="bulk-upload-btn">
                  <Upload className="w-4 h-4 mr-2" />
                  Bulk Upload
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <FileSpreadsheet className="w-5 h-5 text-[#DC2626]" />
                    Bulk Employee Upload
                  </DialogTitle>
                  <DialogDescription>
                    Upload multiple employees at once using an Excel file
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4 mt-4">
                  {/* Step 1: Download Template */}
                  <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <h4 className="font-medium mb-2">Step 1: Download Template</h4>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
                      Download the Excel template and fill in employee details
                    </p>
                    <Button variant="outline" onClick={handleDownloadTemplate} data-testid="download-template-btn">
                      <Download className="w-4 h-4 mr-2" />
                      Download Template
                    </Button>
                  </div>

                  {/* Date Correction tool */}
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <h4 className="font-medium mb-1 text-amber-900">Correct DOB / DOJ in bulk</h4>
                    <p className="text-xs text-amber-700 mb-3">
                      Upload an Excel/CSV with columns <code>email</code> (or <code>employee_id</code>) and <code>date_of_birth</code> / <code>date_of_joining</code>.
                      Indian DD-MM-YYYY format is fully supported.
                    </p>
                    <input
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      onChange={handleBulkDateCorrection}
                      className="hidden"
                      id="bulk-date-correct-input"
                      data-testid="bulk-date-correct-input"
                    />
                    <Button variant="outline" className="border-amber-500 text-amber-700 hover:bg-amber-100"
                      onClick={() => document.getElementById('bulk-date-correct-input')?.click()}
                      data-testid="bulk-date-correct-btn"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Date Correction File
                    </Button>
                  </div>
                  
                  {/* Step 2: Upload File */}
                  <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <h4 className="font-medium mb-2">Step 2: Upload Filled Template</h4>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
                      Upload the completed Excel file (.xlsx)
                    </p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleBulkUpload}
                      className="hidden"
                      id="bulk-upload-input"
                    />
                    <Button 
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadProgress === 'uploading'}
                    data-testid="upload-file-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploadProgress === 'uploading' ? 'Uploading...' : 'Select & Upload File'}
                  </Button>
                </div>
                
                {/* Upload Results */}
                {uploadResults && (
                  <div className="p-4 border rounded-lg" data-testid="bulk-upload-results">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium">Upload Results</h4>
                      {uploadResults.errors?.length > 0 && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const rows = [['Row', 'Email', 'Reason']];
                            uploadResults.errors.forEach(e => rows.push([
                              e.row, e.email || '',
                              (e.error || '').replace(/"/g, '""'),
                            ]));
                            const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
                            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
                            const a = document.createElement('a');
                            a.href = URL.createObjectURL(blob);
                            a.download = `bulk_upload_errors_${new Date().toISOString().slice(0,10)}.csv`;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            URL.revokeObjectURL(a.href);
                          }}
                          data-testid="bulk-upload-errors-csv-btn"
                        >
                          <Download className="w-3 h-3 mr-1" /> Download error report
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-center mb-4">
                      <div className="p-2 bg-slate-100 rounded">
                        <p className="text-2xl font-bold">{uploadResults.total_rows}</p>
                        <p className="text-xs text-slate-500">Total Rows</p>
                      </div>
                      <div className="p-2 bg-green-100 rounded">
                        <p className="text-2xl font-bold text-green-700" data-testid="bulk-upload-success-count">{uploadResults.success?.length || 0}</p>
                        <p className="text-xs text-green-600">Success</p>
                      </div>
                      <div className="p-2 bg-red-100 rounded">
                        <p className="text-2xl font-bold text-red-700" data-testid="bulk-upload-error-count">{uploadResults.errors?.length || 0}</p>
                        <p className="text-xs text-red-600">Errors</p>
                      </div>
                    </div>

                    {uploadResults.errors?.length > 0 && (
                      <div className="mt-2" data-testid="bulk-upload-errors">
                        <p className="text-sm font-medium text-red-600 mb-2">
                          Failed rows ({uploadResults.errors.length}) — fix these in Excel and re-upload only those rows:
                        </p>
                        <div className="max-h-72 overflow-y-auto border rounded-md">
                          <table className="w-full text-xs">
                            <thead className="bg-red-50 sticky top-0">
                              <tr>
                                <th className="text-left px-2 py-1.5 font-semibold text-red-700">Row</th>
                                <th className="text-left px-2 py-1.5 font-semibold text-red-700">Email</th>
                                <th className="text-left px-2 py-1.5 font-semibold text-red-700">Reason</th>
                              </tr>
                            </thead>
                            <tbody>
                              {uploadResults.errors.map((err, idx) => (
                                <tr key={idx} className="border-t border-red-100" data-testid={`bulk-upload-error-row-${err.row}`}>
                                  <td className="px-2 py-1.5 font-mono font-semibold text-red-800 whitespace-nowrap">Row {err.row}</td>
                                  <td className="px-2 py-1.5 text-slate-700 whitespace-nowrap">{err.email || '—'}</td>
                                  <td className="px-2 py-1.5 text-red-700 break-words">{err.error}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {uploadResults.success?.length > 0 && (
                      <div className="mt-3">
                        <p className="text-sm font-medium text-green-600 mb-2">
                          Successfully Added ({uploadResults.success.length}):
                        </p>
                        <div className="max-h-32 overflow-y-auto space-y-1">
                          {uploadResults.success.slice(0, 5).map((emp, idx) => (
                            <div key={idx} className="text-xs p-2 bg-green-50 rounded flex items-center gap-2">
                              <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                              <span>{emp.name} - {emp.email}</span>
                            </div>
                          ))}
                          {uploadResults.success.length > 5 && (
                            <p className="text-xs text-slate-500 pl-5">
                              ...and {uploadResults.success.length - 5} more
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
          
          {/* Add Employee Dialog */}
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="add-employee-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Employee
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add New Employee</DialogTitle>
                <DialogDescription>
                  Fill in the details to onboard a new staff member
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-5 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="entity_id">Entity</Label>
                    <Select
                      value={formData.entity_id || selectedEntity?.entity_id || ''}
                      onValueChange={(value) => setFormData({ ...formData, entity_id: value })}
                    >
                      <SelectTrigger data-testid="entity-select">
                        <SelectValue placeholder="Select entity" />
                      </SelectTrigger>
                      <SelectContent>
                        {entities?.map(entity => (
                          <SelectItem key={entity.entity_id} value={entity.entity_id}>
                            {entity.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="category">Category</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) => setFormData({ ...formData, category: value })}
                    >
                      <SelectTrigger data-testid="category-select">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {(addMasterLists.category?.length ? addMasterLists.category : EMPLOYEE_CATEGORIES).map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {addMasterLists.category?.length > 0 && (
                      <p className="text-[10px] text-slate-400">From this institute's master list</p>
                    )}
                  </div>
                </div>

                {/* Personal Details */}
                <div>
                  <p className="text-sm font-semibold text-blue-600 pb-2 mb-2 border-b">Personal Details</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label>First Name *</Label>
                      <Input value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} required data-testid="first-name-input" />
                    </div>
                    <div className="space-y-1">
                      <Label>Middle Name</Label>
                      <Input value={formData.middle_name} onChange={(e) => setFormData({ ...formData, middle_name: e.target.value })} data-testid="middle-name-input" />
                    </div>
                    <div className="space-y-1">
                      <Label>Last Name *</Label>
                      <Input value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} required data-testid="last-name-input" />
                    </div>
                    <div className="space-y-1">
                      <Label>Email *</Label>
                      <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required data-testid="email-input" />
                    </div>
                    <div className="space-y-1">
                      <Label>Phone</Label>
                      <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} data-testid="phone-input" />
                    </div>
                    <div className="space-y-1">
                      <Label>Gender</Label>
                      <Select value={formData.gender} onValueChange={(v) => setFormData({ ...formData, gender: v })}>
                        <SelectTrigger data-testid="gender-select"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Male">Male</SelectItem>
                          <SelectItem value="Female">Female</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label>Date of Birth</Label>
                      <Input type="date" value={formData.date_of_birth} onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })} data-testid="dob-input" />
                    </div>
                    <div className="space-y-1">
                      <Label>PT Exemptions</Label>
                      <div className="flex items-center gap-4 h-9">
                        <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                          <input type="checkbox" checked={formData.is_handicapped === true} onChange={(e) => setFormData({ ...formData, is_handicapped: e.target.checked })} data-testid="handicap-toggle" /> Handicap
                        </label>
                        <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                          <input type="checkbox" checked={formData.is_senior_citizen === true} onChange={(e) => setFormData({ ...formData, is_senior_citizen: e.target.checked })} data-testid="senior-citizen-toggle" /> Senior Citizen
                        </label>
                      </div>
                    </div>
                    <div className="space-y-1 sm:col-span-2">
                      <Label>Address</Label>
                      <Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder="Full address" data-testid="address-input" />
                    </div>
                  </div>
                </div>

                {/* Employment Details */}
                <div>
                  <p className="text-sm font-semibold text-green-600 pb-2 mb-2 border-b">Employment Details</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label>Department *</Label>
                      {addMasterLists.department?.length ? (
                        <Select value={formData.department} onValueChange={(v) => setFormData({ ...formData, department: v })}>
                          <SelectTrigger data-testid="department-input"><SelectValue placeholder="Select department" /></SelectTrigger>
                          <SelectContent>
                            {addMasterLists.department.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input value={formData.department} onChange={(e) => setFormData({ ...formData, department: e.target.value })} placeholder="e.g., Computer Science" required data-testid="department-input" />
                      )}
                    </div>
                    <div className="space-y-1">
                      <Label>Designation *</Label>
                      {addMasterLists.designation?.length ? (
                        <Select value={formData.designation} onValueChange={(v) => setFormData({ ...formData, designation: v })}>
                          <SelectTrigger data-testid="designation-input"><SelectValue placeholder="Select designation" /></SelectTrigger>
                          <SelectContent>
                            {addMasterLists.designation.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input value={formData.designation} onChange={(e) => setFormData({ ...formData, designation: e.target.value })} placeholder="e.g., Senior Professor" required data-testid="designation-input" />
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label>Date of Joining *</Label>
                      <Input type="date" value={formData.date_of_joining} onChange={(e) => setFormData({ ...formData, date_of_joining: e.target.value })} required data-testid="doj-input" />
                    </div>
                    <div className="space-y-1">
                      <Label>Appointment Type</Label>
                      <Select value={formData.appointment_type} onValueChange={(value) => setFormData({ ...formData, appointment_type: value })}>
                        <SelectTrigger data-testid="appointment-type-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {(addMasterLists.appointment_type?.length ? addMasterLists.appointment_type : APPOINTMENT_TYPES).map(type => <SelectItem key={type} value={type}>{type}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Salary & Allowances */}
                  <p className="text-xs font-semibold text-amber-600 pt-2 border-t">Salary & Allowances (Monthly in INR)</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label>Salary Type</Label>
                      <Select value={formData.salary_type} onValueChange={(v) => setFormData({ ...formData, salary_type: v })}>
                        <SelectTrigger data-testid="salary-type-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="breakdown">Breakdown (Basic + DA + HRA + ...)</SelectItem>
                          <SelectItem value="consolidated">Consolidated (Single flat amount)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {formData.salary_type === 'consolidated' && (
                      <div className="space-y-1">
                        <Label>Consolidated Salary *</Label>
                        <Input type="number" value={formData.consolidated_salary} onChange={(e) => setFormData({ ...formData, consolidated_salary: e.target.value })} placeholder="Flat monthly amount" data-testid="consolidated-salary-input" />
                      </div>
                    )}
                  </div>
                  {formData.salary_type !== 'consolidated' && (
                  <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="space-y-1">
                      <Label>Gross Salary</Label>
                      <Input type="number" value={formData.gross_salary} onChange={(e) => setFormData({ ...formData, gross_salary: e.target.value })} data-testid="gross-salary-input" />
                    </div>
                    <div className="space-y-1">
                      <Label>Basic Salary</Label>
                      <Input type="number" value={formData.basic_salary} onChange={(e) => setFormData({ ...formData, basic_salary: e.target.value })} data-testid="salary-input" />
                    </div>
                    <div className="space-y-1">
                      <Label>AGP</Label>
                      <Input type="number" value={formData.agp} onChange={(e) => setFormData({ ...formData, agp: e.target.value })} placeholder="Academic Grade Pay" data-testid="agp-input" />
                    </div>
                    <div className="space-y-1">
                      <Label>DA %</Label>
                      <Input type="number" step="0.01" value={formData.da_percentage} onChange={(e) => setFormData({ ...formData, da_percentage: e.target.value })} placeholder="e.g. 30 → auto DA & HRA" data-testid="da-pct-input" />
                    </div>
                    <div className="space-y-1">
                      <Label>DA</Label>
                      <Input type="number" value={formData.da} onChange={(e) => setFormData({ ...formData, da: e.target.value })} placeholder={formData.da_percentage ? 'Auto: Gross Basic × DA%' : 'Dearness Allowance'} disabled={!!parseFloat(formData.da_percentage)} data-testid="da-input" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label>HRA</Label>
                      <Input type="number" value={formData.hra} onChange={(e) => setFormData({ ...formData, hra: e.target.value })} placeholder={formData.da_percentage ? 'Blank = auto 10% of Gross Basic' : 'House Rent'} data-testid="hra-input" />
                    </div>
                    <div className="space-y-1">
                      <Label>H.O.D. Allowance</Label>
                      <Input type="number" value={formData.hod_allowance} onChange={(e) => setFormData({ ...formData, hod_allowance: e.target.value })} placeholder="HOD Allowance" data-testid="hod-allow-input" />
                    </div>
                    <div className="space-y-1">
                      <Label>Special Allowance</Label>
                      <Input type="number" value={formData.special_allowance} onChange={(e) => setFormData({ ...formData, special_allowance: e.target.value })} placeholder="Special Allowance" data-testid="special-allow-input" />
                    </div>
                    <div className="space-y-1">
                      <Label>Other Allowance</Label>
                      <Input type="number" value={formData.other_allowance} onChange={(e) => setFormData({ ...formData, other_allowance: e.target.value })} data-testid="other-allow-input" />
                    </div>
                  </div>

                  {/* Deductions */}
                  <p className="text-xs font-semibold text-red-600 pt-2 border-t">Deductions (Monthly in INR)</p>
                  <p className="text-[11px] text-slate-500 -mt-1">Leave a deduction at 0 and tick &quot;auto&quot; to compute per SGI slab rules from the manual template.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="space-y-1">
                      <Label className="flex items-center justify-between">
                        Provident Fund
                        <span className="text-[10px] font-normal flex items-center gap-1">
                          <input type="checkbox" checked={formData.pf_applicable !== false} onChange={(e) => setFormData({ ...formData, pf_applicable: e.target.checked })} data-testid="pf-applicable-toggle" /> auto
                        </span>
                      </Label>
                      <Input type="number" value={formData.provident_fund} onChange={(e) => setFormData({ ...formData, provident_fund: e.target.value })} data-testid="pf-input" placeholder={formData.pf_applicable ? '0 = auto-slab' : 'Not applicable'} disabled={formData.pf_applicable === false && !formData.provident_fund} />
                    </div>
                    <div className="space-y-1">
                      <Label className="flex items-center justify-between">
                        Prof. Tax
                        <span className="text-[10px] font-normal flex items-center gap-1">
                          <input type="checkbox" checked={formData.pt_applicable !== false} onChange={(e) => setFormData({ ...formData, pt_applicable: e.target.checked })} data-testid="pt-applicable-toggle" /> auto
                        </span>
                      </Label>
                      <Input type="number" value={formData.profession_tax} onChange={(e) => setFormData({ ...formData, profession_tax: e.target.value })} data-testid="pt-input" placeholder={formData.pt_applicable ? '0 = auto-slab' : 'Not applicable'} />
                    </div>
                    <div className="space-y-1">
                      <Label>TDS</Label>
                      <Input type="number" value={formData.tds} onChange={(e) => setFormData({ ...formData, tds: e.target.value })} data-testid="tds-input" />
                    </div>
                    <div className="space-y-1">
                      <Label>Bus</Label>
                      <Input type="number" value={formData.bus} onChange={(e) => setFormData({ ...formData, bus: e.target.value })} data-testid="bus-input" />
                    </div>
                    <div className="space-y-1">
                      <Label className="flex items-center justify-between">
                        ESIC
                        <span className="text-[10px] font-normal flex items-center gap-1">
                          <input type="checkbox" checked={formData.esic_applicable === true} onChange={(e) => setFormData({ ...formData, esic_applicable: e.target.checked })} data-testid="esic-applicable-toggle" /> auto
                        </span>
                      </Label>
                      <Input type="number" value={formData.esic} onChange={(e) => setFormData({ ...formData, esic: e.target.value })} data-testid="esic-input" placeholder={formData.esic_applicable ? '0 = auto 0.75% (gross ≤ 21k)' : 'Manual'} />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label>Accommodation</Label>
                      <Input type="number" value={formData.accommodation} onChange={(e) => setFormData({ ...formData, accommodation: e.target.value })} data-testid="accommodation-input" />
                    </div>
                    <div className="space-y-1">
                      <Label>Local Mart</Label>
                      <Input type="number" value={formData.localmart} onChange={(e) => setFormData({ ...formData, localmart: e.target.value })} data-testid="localmart-input" placeholder="Manual entry" />
                    </div>
                    <div className="space-y-1">
                      <Label>Other Deduction</Label>
                      <Input type="number" value={formData.other_deduction} onChange={(e) => setFormData({ ...formData, other_deduction: e.target.value })} data-testid="other-ded-input" />
                    </div>
                  </div>
                  <StatutoryPreview f={formData} claCfg={claMap[formData.entity_id || selectedEntity?.entity_id]} />
                  </>
                  )}

                  {/* Qualifications & Bank */}
                  <p className="text-xs font-semibold text-purple-600 pt-2 border-t">Qualifications & Bank Details</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label>Qualification</Label>
                      <Select value={formData.highest_qualification} onValueChange={(value) => setFormData({ ...formData, highest_qualification: value })}>
                        <SelectTrigger data-testid="qualification-select"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="PhD">PhD</SelectItem>
                          <SelectItem value="M.Tech">M.Tech</SelectItem>
                          <SelectItem value="M.Phil">M.Phil</SelectItem>
                          <SelectItem value="M.Sc">M.Sc</SelectItem>
                          <SelectItem value="MBA">MBA</SelectItem>
                          <SelectItem value="B.Tech">B.Tech</SelectItem>
                          <SelectItem value="B.Sc">B.Sc</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label>Specialization</Label>
                      <Input value={formData.specialization} onChange={(e) => setFormData({ ...formData, specialization: e.target.value })} placeholder="e.g., Machine Learning" data-testid="specialization-input" />
                    </div>
                    <div className="space-y-1">
                      <Label>Experience (Years)</Label>
                      <Input type="number" step="0.5" value={formData.experience_years} onChange={(e) => setFormData({ ...formData, experience_years: e.target.value })} data-testid="experience-input" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label>Bank Name</Label>
                      <Input value={formData.bank_name} onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })} data-testid="bank-name-input" />
                    </div>
                    <div className="space-y-1">
                      <Label>Account Number</Label>
                      <Input value={formData.bank_account} onChange={(e) => setFormData({ ...formData, bank_account: e.target.value })} data-testid="bank-account-input" />
                    </div>
                    <div className="space-y-1">
                      <Label>IFSC Code</Label>
                      <Input value={formData.ifsc_code} onChange={(e) => setFormData({ ...formData, ifsc_code: e.target.value })} data-testid="ifsc-input" />
                    </div>
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="btn-primary" data-testid="submit-employee-btn">
                    Add Employee
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name, email, ID, or department..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="employee-search"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full md:w-[200px]" data-testid="category-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {(categories.length > 0 ? categories : EMPLOYEE_CATEGORIES).map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-[190px]" data-testid="status-filter">
                <UserX className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active" data-testid="status-active">Active Staff</SelectItem>
                <SelectItem value="inactive" data-testid="status-inactive">Exited / Deactivated</SelectItem>
                <SelectItem value="all" data-testid="status-all">All Staff</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Employee Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-[#DC2626]" />
            Staff Directory ({filteredEmployees.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-16 bg-slate-100 dark:bg-slate-800 rounded animate-pulse" />
              ))}
            </div>
          ) : filteredEmployees.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 dark:text-white">No employees found</h3>
              <p className="text-slate-500">Add employees or adjust your filters</p>
            </div>
          ) : (
            <div className="overflow-x-auto responsive-card-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    {canEditEmployee && (
                      <TableHead className="w-10">
                        <input type="checkbox" checked={selectedRows.length === filteredEmployees.length && filteredEmployees.length > 0}
                          onChange={toggleSelectAll} className="rounded" data-testid="select-all-checkbox" />
                      </TableHead>
                    )}
                    <TableHead>Employee</TableHead>
                    <TableHead>Designation</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead className="hidden md:table-cell">Contact</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEmployees.map(emp => (
                    <TableRow key={emp.employee_id} data-testid={`employee-row-${emp.employee_id}`}
                      className={selectedRows.includes(emp.employee_id) ? 'bg-blue-50' : ''}>
                      {canEditEmployee && (
                        <TableCell>
                          <input type="checkbox" checked={selectedRows.includes(emp.employee_id)}
                            onChange={() => toggleRowSelect(emp.employee_id)} className="rounded" />
                        </TableCell>
                      )}
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="w-10 h-10">
                            <AvatarImage src={emp.profile_picture ? `${API_URL}${emp.profile_picture}` : undefined} />
                            <AvatarFallback className="bg-[#DC2626] text-white text-sm">
                              {getInitials(emp.first_name, emp.last_name)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium text-slate-900 dark:text-white">
                              {emp.first_name} {emp.last_name}
                            </p>
                            <code className="text-[10px] text-slate-400">{emp.employee_id}</code>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm font-medium text-slate-800" data-testid={`designation-${emp.employee_id}`}>
                          {emp.designation || <span className="text-slate-400 italic">Not set</span>}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm">{emp.department}</TableCell>
                      <TableCell>
                        <Badge className={getCategoryBadgeColor(emp.category)}>
                          {emp.category}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <div className="space-y-1">
                          <div className="flex items-center gap-1 text-sm text-slate-600 dark:text-slate-400">
                            <Mail className="w-3 h-3" />
                            <span className="truncate max-w-[150px]">{emp.email}</span>
                          </div>
                          {emp.phone && (
                            <div className="flex items-center gap-1 text-sm text-slate-600 dark:text-slate-400">
                              <Phone className="w-3 h-3" />
                              {emp.phone}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={emp.is_active ? 'badge-success' : 'badge-error'}>
                          {emp.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`employee-actions-${emp.employee_id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/employees/${emp.employee_id}`)}>
                              <Eye className="w-4 h-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openEditDialog(emp)} data-testid={`edit-employee-${emp.employee_id}`}>
                              <Edit className="w-4 h-4 mr-2" />
                              Edit Details
                            </DropdownMenuItem>
                            {canEditEmployee && (
                              <DropdownMenuItem onClick={() => openResetSingle(emp)} className="text-amber-700" data-testid={`reset-pwd-${emp.employee_id}`}>
                                <KeyRound className="w-4 h-4 mr-2" />
                                Reset Password
                              </DropdownMenuItem>
                            )}
                            {canExitEmployee && emp.is_active !== false && (
                              <DropdownMenuItem onClick={() => setExitDialogOpen(emp.employee_id)} data-testid={`exit-employee-${emp.employee_id}`}>
                                <UserX className="w-4 h-4 mr-2" />
                                Exit Employee
                              </DropdownMenuItem>
                            )}
                            {emp.is_active === false && canExitEmployee && (
                              <>
                                <DropdownMenuItem onClick={() => handleReactivate(emp.employee_id)} className="text-emerald-700 font-medium" data-testid={`reactivate-employee-${emp.employee_id}`}>
                                  <UserCheck className="w-4 h-4 mr-2" />
                                  Reactivate
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => downloadLetter(emp.employee_id, 'relieving')} data-testid={`relieving-letter-${emp.employee_id}`}>
                                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                                  Relieving Letter
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => downloadLetter(emp.employee_id, 'experience')} data-testid={`experience-letter-${emp.employee_id}`}>
                                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                                  Experience Letter
                                </DropdownMenuItem>
                              </>
                            )}
                            {emp.is_active !== false && (
                              <DropdownMenuItem 
                                className="text-orange-600"
                                onClick={() => handleDelete(emp.employee_id, false)}
                              >
                                <XCircle className="w-4 h-4 mr-2" />
                                Deactivate
                              </DropdownMenuItem>
                            )}
                            {user?.role === 'super_admin' && (
                              <DropdownMenuItem 
                                className="text-red-600 font-medium"
                                onClick={() => handleDelete(emp.employee_id, true)}
                                data-testid={`permanent-delete-${emp.employee_id}`}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Permanent Delete
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Exit Employee Dialog */}
      <Dialog open={!!exitDialogOpen} onOpenChange={() => setExitDialogOpen(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Exit Employee</DialogTitle>
            <DialogDescription>Process employee separation and generate exit documents</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-3">
            <div className="space-y-2">
              <Label>Exit Date</Label>
              <Input type="date" value={exitForm.exit_date} onChange={e => setExitForm({...exitForm, exit_date: e.target.value})} data-testid="exit-date" />
            </div>
            <div className="space-y-2">
              <Label>Reason</Label>
              <Select value={exitForm.reason} onValueChange={v => setExitForm({...exitForm, reason: v})}>
                <SelectTrigger data-testid="exit-reason"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Resignation">Resignation</SelectItem>
                  <SelectItem value="Retirement">Retirement</SelectItem>
                  <SelectItem value="Termination">Termination</SelectItem>
                  <SelectItem value="Contract End">Contract End</SelectItem>
                  <SelectItem value="Transfer">Transfer</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Remarks</Label>
              <Input placeholder="Additional notes..." value={exitForm.remarks} onChange={e => setExitForm({...exitForm, remarks: e.target.value})} data-testid="exit-remarks" />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setExitDialogOpen(null)}>Cancel</Button>
              <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={handleExitEmployee} data-testid="confirm-exit-btn">
                <UserX className="w-4 h-4 mr-1.5" />Process Exit
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rearrange Sequence Dialog */}
      <Dialog open={reorderOpen} onOpenChange={setReorderOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ListOrdered className="w-5 h-5 text-blue-600" />Rearrange Employee Sequence</DialogTitle>
            <DialogDescription>This order is used in the employee list, attendance approval and the salary statement download.</DialogDescription>
          </DialogHeader>
          {seqLock.locked && (
            <div className="rounded-md border border-amber-400 bg-amber-50 p-2.5 flex items-center justify-between gap-2" data-testid="seq-lock-banner">
              <p className="text-xs text-amber-800">
                🔒 Sequence LOCKED{seqLock.locked_by_name ? ` by ${seqLock.locked_by_name}` : ''} — unlock to make changes.
              </p>
              <Button size="sm" variant="outline" className="h-7 border-amber-500 text-amber-700"
                onClick={() => toggleSeqLock('unlock')} data-testid="seq-unlock-btn">Unlock</Button>
            </div>
          )}
          <fieldset disabled={seqLock.locked} className={seqLock.locked ? 'opacity-50 pointer-events-none space-y-3' : 'space-y-3'}>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-slate-500 flex-shrink-0">Quick sort:</Label>
            <Select onValueChange={applyQuickSort}>
              <SelectTrigger className="h-8 text-sm" data-testid="reorder-sort-select"><SelectValue placeholder="Choose sorting..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="dept_category">Department + Category (Teaching first)</SelectItem>
                <SelectItem value="department">Department wise</SelectItem>
                <SelectItem value="designation">Designation wise</SelectItem>
                <SelectItem value="category">Category (Teaching / Non-Teaching)</SelectItem>
                <SelectItem value="name">Name (A–Z)</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" className="h-8 flex-shrink-0"
              onClick={() => setShowDeptSeq(v => !v)} data-testid="dept-seq-toggle">
              Dept Sequence
            </Button>
          </div>
          {showDeptSeq && (
            <div className="border border-blue-200 bg-blue-50/50 rounded-md p-2 space-y-1" data-testid="dept-seq-panel">
              <p className="text-[11px] text-slate-600">Set the department order — staff will be grouped in this sequence (relative order inside each department is kept):</p>
              <div className="max-h-40 overflow-y-auto divide-y bg-white rounded border">
                {deptSeq.map((d, i) => (
                  <div key={d} className="flex items-center gap-2 px-2 py-1 text-xs" data-testid={`dept-seq-row-${i}`}>
                    <span className="w-5 text-slate-400 font-mono">{i + 1}.</span>
                    <span className="flex-1 truncate font-medium">{d}</span>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0" disabled={i === 0}
                      onClick={() => moveDeptSeq(i, -1)} data-testid={`dept-seq-up-${i}`}>
                      <ArrowUp className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0" disabled={i === deptSeq.length - 1}
                      onClick={() => moveDeptSeq(i, 1)} data-testid={`dept-seq-down-${i}`}>
                      <ArrowDown className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
              <Button size="sm" className="h-7 bg-blue-600 hover:bg-blue-700 text-white"
                onClick={applyDeptSequence} data-testid="dept-seq-apply-btn">
                Apply Department Sequence
              </Button>
            </div>
          )}
          {(() => {
            const selCount = reorderList.filter(e => reorderChecked[e.employee_id]).length;
            return (
              <div className="flex items-center gap-2 flex-wrap">
                <Label className="text-xs text-slate-500 flex items-center gap-1.5 flex-shrink-0">
                  <input type="checkbox"
                    checked={reorderList.length > 0 && selCount === reorderList.length}
                    onChange={(ev) => {
                      const m = {};
                      if (ev.target.checked) reorderList.forEach(e => { m[e.employee_id] = true; });
                      setReorderChecked(m);
                    }}
                    data-testid="reorder-select-all" />
                  Selected: {selCount}
                </Label>
                <Input type="number" min="1" max={reorderList.length} placeholder="Position #"
                  className="h-8 w-28 text-sm" value={movePos} onChange={(ev) => setMovePos(ev.target.value)}
                  data-testid="reorder-move-pos" />
                <Button variant="outline" size="sm" className="h-8" onClick={moveSelectedToPosition} data-testid="reorder-move-btn">
                  Move Here
                </Button>
                <Select value={moveDept} onValueChange={setMoveDept}>
                  <SelectTrigger className="h-8 w-36 text-sm" data-testid="reorder-dept-select"><SelectValue placeholder="Department…" /></SelectTrigger>
                  <SelectContent>
                    {[...new Set(reorderList.map(e => e.department).filter(Boolean))].sort().map(d => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" className="h-8" onClick={moveSelectedToDepartment} data-testid="reorder-move-dept-btn">
                  Move to Dept
                </Button>
              </div>
            );
          })()}
          <div className="max-h-[45vh] overflow-y-auto border rounded-md divide-y" data-testid="reorder-list">
            {reorderList.map((emp, idx) => (
              <div key={emp.employee_id} className={`flex items-center gap-2 px-2 py-1.5 text-sm ${reorderChecked[emp.employee_id] ? 'bg-blue-50 dark:bg-blue-950/30' : ''}`}>
                <input type="checkbox" checked={!!reorderChecked[emp.employee_id]}
                  onChange={(ev) => setReorderChecked({ ...reorderChecked, [emp.employee_id]: ev.target.checked })}
                  data-testid={`reorder-check-${emp.employee_id}`} />
                <span className="w-7 text-xs text-slate-400 font-mono">{idx + 1}.</span>
                <span className="flex-1 truncate">{emp.first_name} {emp.last_name}</span>
                <span className="text-xs text-blue-500 truncate max-w-[90px]">{emp.department || ''}</span>
                <span className="text-xs text-slate-400 truncate max-w-[110px]">{emp.designation || emp.category || ''}</span>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" disabled={idx === 0} onClick={() => moveRow(idx, -1)} data-testid={`reorder-up-${emp.employee_id}`}>
                  <ArrowUp className="w-3.5 h-3.5" />
                </Button>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" disabled={idx === reorderList.length - 1} onClick={() => moveRow(idx, 1)} data-testid={`reorder-down-${emp.employee_id}`}>
                  <ArrowDown className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
            {reorderList.length === 0 && <p className="text-xs text-slate-500 text-center py-4">No employees to arrange</p>}
          </div>
          </fieldset>
          <div className="flex justify-end gap-3 pt-1">
            <Button variant="outline" onClick={() => setReorderOpen(false)}>Cancel</Button>
            <Button className="btn-primary" onClick={handleSaveOrder} disabled={savingOrder || seqLock.locked} data-testid="save-order-btn">
              {savingOrder && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}{seqLock.locked ? 'Locked' : 'Save & Lock Order'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Employee Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={(open) => { setEditDialogOpen(open); if (!open) setEditingEmployee(null); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Employee Details</DialogTitle>
            <DialogDescription>Update employee information. Changes will reflect across the entire application.</DialogDescription>
          </DialogHeader>
          {editingEmployee && (
            <div className="space-y-4 mt-3">
              <div>
                <Label className="text-xs font-semibold text-blue-700">Branch / Institute</Label>
                <Select value={editingEmployee.entity_id || ''} onValueChange={(v) => setEditingEmployee({...editingEmployee, entity_id: v})}>
                  <SelectTrigger data-testid="edit-entity"><SelectValue placeholder="Select branch" /></SelectTrigger>
                  <SelectContent>
                    {entities?.map(entity => (
                      <SelectItem key={entity.entity_id} value={entity.entity_id}>{entity.name} {entity.code ? `(${entity.code})` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-slate-500 mt-0.5">Change this to move the employee to a different branch — attendance, payroll and geofence will follow the new branch.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div><Label className="text-xs">First Name</Label><Input value={editingEmployee.first_name || ''} onChange={(e) => setEditingEmployee({...editingEmployee, first_name: e.target.value})} data-testid="edit-first-name" /></div>
                <div><Label className="text-xs">Middle Name</Label><Input value={editingEmployee.middle_name || ''} onChange={(e) => setEditingEmployee({...editingEmployee, middle_name: e.target.value})} /></div>
                <div><Label className="text-xs">Last Name</Label><Input value={editingEmployee.last_name || ''} onChange={(e) => setEditingEmployee({...editingEmployee, last_name: e.target.value})} data-testid="edit-last-name" /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label className="text-xs">Email</Label><Input value={editingEmployee.email || ''} onChange={(e) => setEditingEmployee({...editingEmployee, email: e.target.value})} data-testid="edit-email" /></div>
                <div><Label className="text-xs">Phone</Label><Input value={editingEmployee.phone || ''} onChange={(e) => setEditingEmployee({...editingEmployee, phone: e.target.value})} data-testid="edit-phone" /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-[#DC2626]">Designation *</Label>
                  <Input value={editingEmployee.designation || ''} onChange={(e) => setEditingEmployee({...editingEmployee, designation: e.target.value})} placeholder="e.g., Associate Professor" data-testid="edit-designation" className="border-[#DC2626]/30 focus:border-[#DC2626]" />
                </div>
                <div><Label className="text-xs">Department</Label><Input value={editingEmployee.department || ''} onChange={(e) => setEditingEmployee({...editingEmployee, department: e.target.value})} data-testid="edit-department" /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Category</Label>
                  <Select value={editingEmployee.category || ''} onValueChange={(v) => setEditingEmployee({...editingEmployee, category: v})}>
                    <SelectTrigger data-testid="edit-category"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {[...EMPLOYEE_CATEGORIES, ...categories.filter(c => !EMPLOYEE_CATEGORIES.includes(c))].map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Appointment Type</Label>
                  <Select value={editingEmployee.appointment_type || ''} onValueChange={(v) => setEditingEmployee({...editingEmployee, appointment_type: v})}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {APPOINTMENT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div><Label className="text-xs">Gender</Label><Input value={editingEmployee.gender || ''} onChange={(e) => setEditingEmployee({...editingEmployee, gender: e.target.value})} /></div>
                <div><Label className="text-xs">Qualification</Label><Input value={editingEmployee.highest_qualification || ''} onChange={(e) => setEditingEmployee({...editingEmployee, highest_qualification: e.target.value})} /></div>
                <div><Label className="text-xs">Experience (yrs)</Label><Input type="number" step="0.5" value={editingEmployee.experience_years || 0} onChange={(e) => setEditingEmployee({...editingEmployee, experience_years: parseFloat(e.target.value) || 0})} /></div>
              </div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                  <input type="checkbox" checked={editingEmployee.is_handicapped === true} onChange={(e) => setEditingEmployee({...editingEmployee, is_handicapped: e.target.checked})} data-testid="edit-handicap" /> Handicap (PT NIL)
                </label>
                <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                  <input type="checkbox" checked={editingEmployee.is_senior_citizen === true} onChange={(e) => setEditingEmployee({...editingEmployee, is_senior_citizen: e.target.checked})} data-testid="edit-senior-citizen" /> Senior Citizen (PT NIL)
                </label>
              </div>

              {/* Attendance Overrides — per-employee custom timings */}
              <p className="text-xs font-semibold text-indigo-600 pt-3 border-t">Attendance Overrides (blank = use institute default)</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Late-Mark Cutoff (HH:MM)</Label>
                  <Input placeholder="e.g. 10:30" value={editingEmployee.late_check_in_after || ''} onChange={(e) => setEditingEmployee({...editingEmployee, late_check_in_after: e.target.value})} data-testid="edit-late-cutoff" />
                  <p className="text-[10px] text-slate-500 mt-0.5">Check-ins on or before this time won&apos;t be marked late for this employee.</p>
                </div>
                <div>
                  <Label className="text-xs">Half-Day Cutoff (HH:MM)</Label>
                  <Input placeholder="e.g. 12:00" value={editingEmployee.morning_half_day_after || ''} onChange={(e) => setEditingEmployee({...editingEmployee, morning_half_day_after: e.target.value})} data-testid="edit-halfday-cutoff" />
                  <p className="text-[10px] text-slate-500 mt-0.5">Check-ins after this time will count as a morning half-day.</p>
                </div>
                <div>
                  <Label className="text-xs">Face-Match Tolerance</Label>
                  <Input type="number" step="0.01" min="0.5" max="0.55" placeholder="0.50 (default)" value={editingEmployee.face_match_tolerance ?? ''} onChange={(e) => setEditingEmployee({...editingEmployee, face_match_tolerance: e.target.value === '' ? null : parseFloat(e.target.value)})} data-testid="edit-face-tolerance" />
                  <p className="text-[10px] text-slate-500 mt-0.5">Rarely needed. Bump to 0.52-0.55 if face keeps failing to match despite good lighting. Max 0.55.</p>
                </div>
              </div>

              {/* Salary Section */}
              <p className="text-xs font-semibold text-emerald-600 pt-3 border-t">Salary Components (Monthly ₹)</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label className="text-xs">Salary Type</Label>
                  <Select value={editingEmployee.salary_type || 'breakdown'} onValueChange={(v) => setEditingEmployee({...editingEmployee, salary_type: v})}>
                    <SelectTrigger data-testid="edit-salary-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="breakdown">Breakdown</SelectItem>
                      <SelectItem value="consolidated">Consolidated</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {editingEmployee.salary_type === 'consolidated' && (
                  <div><Label className="text-xs">Consolidated Salary</Label><Input type="number" value={editingEmployee.consolidated_salary ?? 0} onChange={(e) => setEditingEmployee({...editingEmployee, consolidated_salary: parseFloat(e.target.value) || 0})} data-testid="edit-consolidated" /></div>
                )}
              </div>
              {editingEmployee.salary_type !== 'consolidated' && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div><Label className="text-xs">Pay (Basic)</Label><Input type="number" value={editingEmployee.basic_salary ?? 0} onChange={(e) => setEditingEmployee({...editingEmployee, basic_salary: parseFloat(e.target.value) || 0})} data-testid="edit-basic" /></div>
                    <div><Label className="text-xs">AGP</Label><Input type="number" value={editingEmployee.agp ?? 0} onChange={(e) => setEditingEmployee({...editingEmployee, agp: parseFloat(e.target.value) || 0})} data-testid="edit-agp" /></div>
                    <div><Label className="text-xs">DA %</Label><Input type="number" step="0.01" value={editingEmployee.da_percentage ?? 0} onChange={(e) => setEditingEmployee({...editingEmployee, da_percentage: parseFloat(e.target.value) || 0})} placeholder="e.g. 30 → auto DA & HRA" data-testid="edit-da-pct" /></div>
                    <div><Label className="text-xs">D.A. {editingEmployee.da_percentage > 0 ? '(auto: Gross Basic × DA%)' : ''}</Label><Input type="number" value={editingEmployee.da ?? 0} onChange={(e) => setEditingEmployee({...editingEmployee, da: parseFloat(e.target.value) || 0})} disabled={editingEmployee.da_percentage > 0} data-testid="edit-da" /></div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div><Label className="text-xs">H.R.A. {editingEmployee.da_percentage > 0 ? '(blank = auto 10% Gross Basic)' : ''}</Label><Input type="number" value={editingEmployee.hra ?? 0} onChange={(e) => setEditingEmployee({...editingEmployee, hra: parseFloat(e.target.value) || 0})} data-testid="edit-hra" /></div>
                    <div><Label className="text-xs">H.O.D. Allowance</Label><Input type="number" value={editingEmployee.hod_allowance ?? 0} onChange={(e) => setEditingEmployee({...editingEmployee, hod_allowance: parseFloat(e.target.value) || 0})} data-testid="edit-hod-allow" /></div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div><Label className="text-xs">Special Allowance</Label><Input type="number" value={editingEmployee.special_allowance ?? 0} onChange={(e) => setEditingEmployee({...editingEmployee, special_allowance: parseFloat(e.target.value) || 0})} data-testid="edit-special-allow" /></div>
                    <div><Label className="text-xs">Other Allowance</Label><Input type="number" value={editingEmployee.other_allowance ?? 0} onChange={(e) => setEditingEmployee({...editingEmployee, other_allowance: parseFloat(e.target.value) || 0})} data-testid="edit-other-allow" /></div>
                  </div>
                </>
              )}

              {/* Deductions */}
              <p className="text-xs font-semibold text-red-600 pt-3 border-t">Deductions (Monthly ₹)</p>
              <p className="text-[10px] text-slate-500 -mt-1">Leave at 0 with &quot;auto&quot; ticked → SGI slab formulas apply automatically (matches manual template).</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs flex items-center justify-between">Provident Fund
                    <span className="text-[10px] font-normal flex items-center gap-1"><input type="checkbox" checked={editingEmployee.pf_applicable !== false} onChange={(e) => setEditingEmployee({...editingEmployee, pf_applicable: e.target.checked})} data-testid="edit-pf-applicable" /> auto</span>
                  </Label>
                  <Input type="number" value={editingEmployee.provident_fund ?? 0} onChange={(e) => setEditingEmployee({...editingEmployee, provident_fund: parseFloat(e.target.value) || 0})} data-testid="edit-pf" placeholder={editingEmployee.pf_applicable === false ? 'N/A' : '0 = auto'} />
                </div>
                <div>
                  <Label className="text-xs flex items-center justify-between">Prof. Tax
                    <span className="text-[10px] font-normal flex items-center gap-1"><input type="checkbox" checked={editingEmployee.pt_applicable !== false} onChange={(e) => setEditingEmployee({...editingEmployee, pt_applicable: e.target.checked})} data-testid="edit-pt-applicable" /> auto</span>
                  </Label>
                  <Input type="number" value={editingEmployee.profession_tax ?? 0} onChange={(e) => setEditingEmployee({...editingEmployee, profession_tax: parseFloat(e.target.value) || 0})} data-testid="edit-pt" placeholder={editingEmployee.pt_applicable === false ? 'N/A' : '0 = auto'} />
                </div>
                <div>
                  <Label className="text-xs">Local Mart (manual entry)</Label>
                  <Input type="number" value={editingEmployee.localmart ?? 0} onChange={(e) => setEditingEmployee({...editingEmployee, localmart: parseFloat(e.target.value) || 0})} data-testid="edit-localmart" placeholder="Manual entry" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs flex items-center justify-between">ESIC
                    <span className="text-[10px] font-normal flex items-center gap-1"><input type="checkbox" checked={editingEmployee.esic_applicable === true} onChange={(e) => setEditingEmployee({...editingEmployee, esic_applicable: e.target.checked})} data-testid="edit-esic-applicable" /> auto</span>
                  </Label>
                  <Input type="number" value={editingEmployee.esic ?? 0} onChange={(e) => setEditingEmployee({...editingEmployee, esic: parseFloat(e.target.value) || 0})} data-testid="edit-esic" placeholder={editingEmployee.esic_applicable ? '0 = auto 0.75%' : 'Manual'} />
                </div>
                <div><Label className="text-xs">Bus</Label><Input type="number" value={editingEmployee.bus ?? 0} onChange={(e) => setEditingEmployee({...editingEmployee, bus: parseFloat(e.target.value) || 0})} data-testid="edit-bus" /></div>
                <div><Label className="text-xs">Staff Quarters</Label><Input type="number" value={editingEmployee.accommodation ?? 0} onChange={(e) => setEditingEmployee({...editingEmployee, accommodation: parseFloat(e.target.value) || 0})} data-testid="edit-accommodation" /></div>
                <div><Label className="text-xs">TDS</Label><Input type="number" value={editingEmployee.tds ?? 0} onChange={(e) => setEditingEmployee({...editingEmployee, tds: parseFloat(e.target.value) || 0})} data-testid="edit-tds" /></div>
                <div><Label className="text-xs">Other Deduction</Label><Input type="number" value={editingEmployee.other_deduction ?? 0} onChange={(e) => setEditingEmployee({...editingEmployee, other_deduction: parseFloat(e.target.value) || 0})} data-testid="edit-other-ded" /></div>
              </div>
              <StatutoryPreview f={editingEmployee} claCfg={claMap[editingEmployee.entity_id]} />

              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
                <Button className="bg-[#DC2626] hover:bg-[#B91C1C] text-white" onClick={handleEditEmployee} data-testid="save-edit-employee-btn">
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Update Fields Dialog — supports designation + all salary + deduction fields */}
      <Dialog open={bulkDesigOpen} onOpenChange={setBulkDesigOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Bulk Update — {selectedRows.length} employee(s)</DialogTitle>
            <DialogDescription>Tick the checkbox next to each field you want to update. Only ticked fields are applied.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-3">
            {/* Selected preview */}
            <div className="text-sm text-slate-600">
              <div className="max-h-24 overflow-y-auto space-y-0.5">
                {selectedRows.slice(0, 8).map((id) => {
                  const emp = employees.find((e) => e.employee_id === id);
                  return emp ? (
                    <div key={id} className="flex items-center justify-between text-[11px] bg-slate-50 dark:bg-slate-800 p-1 rounded">
                      <span>{emp.first_name} {emp.last_name}</span>
                      <span className="text-slate-400">{emp.designation || '—'}</span>
                    </div>
                  ) : null;
                })}
                {selectedRows.length > 8 && <div className="text-[11px] text-slate-500">…and {selectedRows.length - 8} more</div>}
              </div>
            </div>

            {/* Bulk edit fields — rendered inline (nested components cause input focus loss on every render) */}
            <p className="text-xs font-semibold text-slate-600 border-t pt-2">Identity</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">
              {[
                { k: 'designation', label: 'Designation' },
                { k: 'department', label: 'Department' },
                { k: 'category', label: 'Category' },
                { k: 'appointment_type', label: 'Appointment Type' },
              ].map((f) => (
                <div key={f.k} className="flex items-center gap-2">
                  <input type="checkbox" checked={!!bulkEditEnabled[f.k]} onChange={(e) => setBulkEditEnabled({ ...bulkEditEnabled, [f.k]: e.target.checked })} data-testid={`bulk-en-${f.k}`} />
                  <Label className="text-xs flex-shrink-0 w-32">{f.label}</Label>
                  <Input value={bulkEditData[f.k] ?? ''} disabled={!bulkEditEnabled[f.k]} onChange={(e) => setBulkEditData({ ...bulkEditData, [f.k]: e.target.value })} data-testid={`bulk-val-${f.k}`} className="h-8 text-sm" />
                </div>
              ))}
            </div>

            <p className="text-xs font-semibold text-blue-700 border-t pt-2">Move to Branch / Institute</p>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={!!bulkEditEnabled.entity_id} onChange={(e) => setBulkEditEnabled({ ...bulkEditEnabled, entity_id: e.target.checked })} data-testid="bulk-en-entity_id" />
              <Label className="text-xs flex-shrink-0 w-32">Branch</Label>
              <Select value={bulkEditData.entity_id || ''} onValueChange={(v) => setBulkEditData({ ...bulkEditData, entity_id: v })} disabled={!bulkEditEnabled.entity_id}>
                <SelectTrigger className="h-8 text-sm" data-testid="bulk-val-entity_id"><SelectValue placeholder="Select target branch" /></SelectTrigger>
                <SelectContent>
                  {entities?.map((entity) => (
                    <SelectItem key={entity.entity_id} value={entity.entity_id}>{entity.name} {entity.code ? `(${entity.code})` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <p className="text-xs font-semibold text-indigo-600 border-t pt-2">Attendance Overrides (blank = institute default)</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">
              {[
                { k: 'late_check_in_after', label: 'Late-Mark Cutoff' },
                { k: 'morning_half_day_after', label: 'Half-Day Cutoff' },
              ].map((f) => (
                <div key={f.k} className="flex items-center gap-2">
                  <input type="checkbox" checked={!!bulkEditEnabled[f.k]} onChange={(e) => setBulkEditEnabled({ ...bulkEditEnabled, [f.k]: e.target.checked })} data-testid={`bulk-en-${f.k}`} />
                  <Label className="text-xs flex-shrink-0 w-32">{f.label}</Label>
                  <Input placeholder="HH:MM (e.g. 10:30)" value={bulkEditData[f.k] ?? ''} disabled={!bulkEditEnabled[f.k]} onChange={(e) => setBulkEditData({ ...bulkEditData, [f.k]: e.target.value })} data-testid={`bulk-val-${f.k}`} className="h-8 text-sm" />
                </div>
              ))}
            </div>

            <p className="text-xs font-semibold text-emerald-700 border-t pt-2">Salary Components (₹)</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">
              {[
                { k: 'basic_salary', label: 'Pay (Basic)' },
                { k: 'agp', label: 'AGP' },
                { k: 'da_percentage', label: 'DA %' },
                { k: 'da', label: 'D.A.' },
                { k: 'hra', label: 'H.R.A.' },
                { k: 'hod_allowance', label: 'H.O.D. Allowance' },
                { k: 'special_allowance', label: 'Special Allowance' },
                { k: 'other_allowance', label: 'Other Allowance' },
                { k: 'consolidated_salary', label: 'Consolidated Salary' },
              ].map((f) => (
                <div key={f.k} className="flex items-center gap-2">
                  <input type="checkbox" checked={!!bulkEditEnabled[f.k]} onChange={(e) => setBulkEditEnabled({ ...bulkEditEnabled, [f.k]: e.target.checked })} data-testid={`bulk-en-${f.k}`} />
                  <Label className="text-xs flex-shrink-0 w-32">{f.label}</Label>
                  <Input type="number" value={bulkEditData[f.k] ?? ''} disabled={!bulkEditEnabled[f.k]} onChange={(e) => setBulkEditData({ ...bulkEditData, [f.k]: e.target.value })} data-testid={`bulk-val-${f.k}`} className="h-8 text-sm" />
                </div>
              ))}
            </div>

            <p className="text-xs font-semibold text-red-700 border-t pt-2">Deductions (₹) — set to 0 + toggle applicability for slab-auto</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">
              {[
                { k: 'provident_fund', label: 'Provident Fund', type: 'num' },
                { k: 'pf_applicable', label: 'PF applicable', type: 'bool' },
                { k: 'esic', label: 'ESIC', type: 'num' },
                { k: 'esic_applicable', label: 'ESIC auto (0.75%)', type: 'bool' },
                { k: 'profession_tax', label: 'Prof. Tax', type: 'num' },
                { k: 'pt_applicable', label: 'Prof. Tax applicable', type: 'bool' },
                { k: 'localmart', label: 'Local Mart (manual)', type: 'num' },
                { k: 'bus', label: 'Bus', type: 'num' },
                { k: 'accommodation', label: 'Staff Quarters', type: 'num' },
                { k: 'tds', label: 'TDS', type: 'num' },
                { k: 'other_deduction', label: 'Other Deduction', type: 'num' },
              ].map((f) => (
                <div key={f.k} className="flex items-center gap-2">
                  <input type="checkbox" checked={!!bulkEditEnabled[f.k]} onChange={(e) => setBulkEditEnabled({ ...bulkEditEnabled, [f.k]: e.target.checked })} data-testid={`bulk-en-${f.k}`} />
                  <Label className="text-xs flex-shrink-0 w-32">{f.label}</Label>
                  {f.type === 'bool' ? (
                    <Select value={String(bulkEditData[f.k] ?? 'true')} onValueChange={(v) => setBulkEditData({ ...bulkEditData, [f.k]: v === 'true' })} disabled={!bulkEditEnabled[f.k]}>
                      <SelectTrigger className="h-8 text-sm" data-testid={`bulk-val-${f.k}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="true">Yes (applicable)</SelectItem>
                        <SelectItem value="false">No (not applicable)</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input type="number" value={bulkEditData[f.k] ?? ''} disabled={!bulkEditEnabled[f.k]} onChange={(e) => setBulkEditData({ ...bulkEditData, [f.k]: e.target.value })} data-testid={`bulk-val-${f.k}`} className="h-8 text-sm" />
                  )}
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-3 border-t pt-3">
              <Button variant="outline" onClick={() => { setBulkDesigOpen(false); setBulkEditData({}); setBulkEditEnabled({}); }}>Cancel</Button>
              <Button
                className="bg-[#DC2626] hover:bg-[#B91C1C] text-white"
                onClick={handleBulkFieldUpdate}
                disabled={Object.values(bulkEditEnabled).filter(Boolean).length === 0}
                data-testid="confirm-bulk-fields-btn"
              >
                Apply to {selectedRows.length} employee(s)
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog — single (inline JSON) or bulk (Excel) */}
      <Dialog open={resetOpen} onOpenChange={(o) => { if (!o && !resetting) { setResetOpen(false); setResetTargets([]); setResetResult(null); } }}>
        <DialogContent className="max-w-lg" data-testid="emp-reset-pwd-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-800">
              <KeyRound className="w-5 h-5" />
              {resetResult ? 'New Credentials' : `Reset Password${resetTargets.length > 1 ? `s (${resetTargets.length} employees)` : ''}`}
            </DialogTitle>
            {!resetResult && (
              <DialogDescription>
                A strong 10-character password will be generated for {resetTargets.length === 1 ? 'this employee' : 'each selected employee'}.
                The affected user{resetTargets.length > 1 ? 's' : ''} will be flagged to change password on next login.
                {resetTargets.length === 1
                  ? " You'll see the new password on this screen with a Copy button."
                  : " You'll receive an Excel file with all new credentials."}
              </DialogDescription>
            )}
          </DialogHeader>
          {resetResult ? (
            <div className="space-y-3">
              <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-emerald-900 text-sm">
                Password reset successful. Share the credentials below with the employee — this password is shown <strong>only once</strong>.
              </div>
              {(resetResult.credentials || []).map((cred) => (
                <div key={cred.employee_id} className="border rounded-md p-3 space-y-2 bg-slate-50 dark:bg-slate-900" data-testid={`emp-reset-result-${cred.employee_id}`}>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div className="text-slate-500">Name</div>
                    <div className="col-span-2 font-medium">{cred.name || cred.employee_id}</div>
                    <div className="text-slate-500">Email (Username)</div>
                    <div className="col-span-2 font-mono text-xs select-all break-all">{cred.email}</div>
                    <div className="text-slate-500">Password</div>
                    <div className="col-span-2 font-mono select-all font-bold text-base tracking-wide" data-testid={`emp-reset-result-password-${cred.employee_id}`}>{cred.password}</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => copyResetCred(cred)} className="w-full" data-testid={`emp-reset-copy-${cred.employee_id}`}>
                    <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy Login + Email + Password
                  </Button>
                </div>
              ))}
              <div className="flex justify-end">
                <Button onClick={() => { setResetOpen(false); setResetTargets([]); setResetResult(null); }} className="bg-slate-700 hover:bg-slate-800 text-white" data-testid="emp-reset-done">
                  Done
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-2 text-sm">
                <div className="bg-amber-50 border border-amber-200 rounded p-3 text-amber-900 flex items-start gap-2">
                  <ShieldAlert className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong>This overwrites existing passwords.</strong> The affected user{resetTargets.length > 1 ? 's' : ''} will not be able to login with their old password.
                  </div>
                </div>
                <div className="max-h-40 overflow-y-auto border rounded">
                  {resetTargets.map((t) => (
                    <div key={t.employee_id} className="flex justify-between text-xs px-3 py-1.5 border-b last:border-b-0">
                      <span className="font-medium">{t.name || t.employee_id}</span>
                      <span className="text-slate-500">{t.email || <span className="text-red-500">(no email — skipped)</span>}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-2">
                <Button variant="ghost" onClick={() => { setResetOpen(false); setResetTargets([]); }} disabled={resetting}>Cancel</Button>
                <Button
                  onClick={submitReset}
                  disabled={resetting || resetTargets.length === 0}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="emp-reset-pwd-confirm"
                >
                  {resetting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <KeyRound className="w-4 h-4 mr-1" />}
                  {resetTargets.length === 1 ? 'Reset Password' : 'Reset & Download Excel'}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
